import { Action, Dispatch } from 'redux';
import { isRacker } from '@rss-engineering/fabric-ui-session';
import {
  COLLECTIONS,
  PROCESS_INSTANCES_URL,
  RequestId,
  RESOURCES_URL,
  STEP_1,
  TICKETS_URL,
  NAMESPACE_PREFERENCES,
  MAXIMUM_RESOURCES_LIMIT,
} from 'appConstants';
import {
  AppState,
  AttachmentItems,
  clearCollection,
  createCollection,
  openInterceptSurvey,
  deletePortalPreferences,
} from 'reducers';
import {
  AccountUtils,
  ApiRequest,
  AttachmentUtils,
  DeviceUtils,
  FeatureFlags,
  history,
  ImmutableQuery,
  MiscUtils,
  NewTicketUtils,
  PreferencesUtil,
  RecipientUtils,
} from 'services';
import {
  Account,
  Device,
  Recipient,
  NewTicketQueryParams,
  CloudGroupUser,
  SubmitTicketQueryParams,
} from 'types';

export interface ApiError {
  readonly errorCode?: number;
  readonly errorMsg: string;
}

/* Types */
export interface NewTicketAction extends Action {
  readonly account?: Account;
  readonly category?: string;
  readonly description?: string;
  readonly devices?: Device[];
  readonly errorCode?: number;
  readonly errorMsg?: string;
  readonly errorInFetchingBPIT?: boolean;
  readonly isDeviceRequired?: boolean;
  readonly product?: string;
  readonly recipient?: Recipient;
  readonly recipients?: Recipient[] | CloudGroupUser[];
  readonly retryFetchingBPIT?: boolean;
  readonly severity?: string;
  readonly step?: number;
  readonly subject?: string;
  readonly bpitTemplateId?: string;
}

export interface Comment {
  readonly text: string;
  readonly attachments?: AttachmentItems[];
}

export interface NewTicketPayLoad {
  readonly accountId: string;
  readonly category: string;
  readonly comment: Comment;
  readonly recipients?: string[];
  readonly resources?: string[];
  readonly severity: string;
  readonly subcategory: string;
  readonly subject: string;
}

export interface NewTicket {
  readonly account: Account;
  readonly bpitTemplateId: string;
  readonly devices: Device[];
  readonly recipients: Recipient[];
  readonly selectedStep: number;
  readonly payload: NewTicketPayLoad;
  readonly errorInFetchingBPIT: boolean;
  readonly isDeviceRequired?: boolean;
  readonly retryFetchingBPIT: boolean;
}

/* Initial State */

const initialState: NewTicket = {
  account: {} as Account,
  bpitTemplateId: '',
  devices: [] as Device[],
  recipients: [],
  selectedStep: STEP_1,
  payload: {
    accountId: '',
    category: '',
    comment: {
      text: '',
    },
    recipients: [] as string[],
    resources: [] as string[],
    severity: '',
    subcategory: '',
    subject: '',
  },
  isDeviceRequired: false,
  errorInFetchingBPIT: false,
  retryFetchingBPIT: false,
};
/* Actions */

enum Actions {
  CLEAR_STEP_2 = 'CLEAR_STEP_2',
  REMOVE_RECIPIENT = 'REMOVE_RECIPIENT',
  RESET_NEW_TICKET_STATE = 'RESET_NEW_TICKET_STATE',
  SET_RESUME_CREATE_TICKET_STATE = 'SET_RESUME_CREATE_TICKET_STATE',
  SET_ACCOUNT = 'SET_ACCOUNT',
  SET_BPIT_TEMPLATE_ID = 'SET_BPIT_TEMPLATE_ID',
  SET_BPIT_TEMPLATE = 'SET_BPIT_TEMPLATE',
  SET_CATEGORY = 'SET_CATEGORY',
  SET_DESCRIPTION = 'SET_DESCRIPTION',
  SET_DEVICES = 'SET_DEVICES',
  SET_ERROR_IN_FETCHING_BPIT = 'SET_ERROR_IN_BPIT',
  SET_IS_DEVICE_REQUIRED = 'SET_IS_DEVICE_REQUIRED',
  SET_NEW_RECIPIENTS = 'SET_NEW_RECIPIENTS',
  SET_PRODUCT = 'SET_PRODUCT',
  SET_REACH_TEMPLATE = 'SET_REACH_TEMPLATE',
  SET_RETRY_FETCHING_BPIT = 'SET_RETRY_FETCHING_BPIT',
  SET_SEVERITY = 'SET_SEVERITY',
  SET_STEP = 'SET_STEP',
  SET_SUBJECT = 'SET_SUBJECT',
  SUBMIT_TICKET_ERROR = 'SUBMIT_TICKET_ERROR',
  SUBMIT_TICKET_START = 'SUBMIT_TICKET_START',
  SUBMIT_TICKET_SUCCESS = 'SUBMIT_TICKET_SUCCESS',
}

const clearStep2 = (): NewTicketAction => ({
  type: Actions.CLEAR_STEP_2,
});

const removeRecipient = (recipient: Recipient): NewTicketAction => ({
  type: Actions.REMOVE_RECIPIENT,
  recipient,
});

const resetNewTicketState = (): NewTicketAction => ({
  type: Actions.RESET_NEW_TICKET_STATE,
});

const setAccount = (account: Account): NewTicketAction => ({
  type: Actions.SET_ACCOUNT,
  account,
});

const setCategory = (category: string): NewTicketAction => ({
  type: Actions.SET_CATEGORY,
  category,
});

const setDescription = (description: string): NewTicketAction => ({
  type: Actions.SET_DESCRIPTION,
  description,
});

const setDevices = (devices: Device[]): NewTicketAction => ({
  type: Actions.SET_DEVICES,
  devices,
});

const setNewRecipients = (
  recipients: Recipient[] | CloudGroupUser[],
): NewTicketAction => ({
  type: Actions.SET_NEW_RECIPIENTS,
  recipients,
});

const setProduct = (product: string): NewTicketAction => ({
  type: Actions.SET_PRODUCT,
  product,
});

const setReachTemplate = (
  category: string,
  description: string | string[],
  product: string,
  subject: string,
): NewTicketAction => ({
  type: Actions.SET_REACH_TEMPLATE,
  category,
  description: Array.isArray(description) ? description.join(' ') : description,
  product,
  subject,
});

const setBpitTemplate = (
  category: string,
  product: string,
  bpitTemplateId: string,
): NewTicketAction => ({
  type: Actions.SET_BPIT_TEMPLATE,
  category,
  product,
  bpitTemplateId,
});

const setResumeTicket = (
  category: string,
  severity?: string,
  subject?: string,
  description?: string,
): NewTicketAction => ({
  type: Actions.SET_RESUME_CREATE_TICKET_STATE,
  category,
  severity,
  subject,
  description,
});

const setSeverity = (severity: string): NewTicketAction => ({
  type: Actions.SET_SEVERITY,
  severity,
});

const setStep = (step: number): NewTicketAction => ({
  type: Actions.SET_STEP,
  step,
});

const setSubject = (subject: string): NewTicketAction => ({
  type: Actions.SET_SUBJECT,
  subject,
});

const setBpitTemplateId = (bpitTemplateId: string): NewTicketAction => ({
  type: Actions.SET_BPIT_TEMPLATE_ID,
  bpitTemplateId,
});

const setErrorInFetchingBPIT = (
  errorInFetchingBPIT: boolean,
): NewTicketAction => ({
  type: Actions.SET_ERROR_IN_FETCHING_BPIT,
  errorInFetchingBPIT,
});

const setIsDeviceRequired = (isDeviceRequired: boolean): NewTicketAction => ({
  type: Actions.SET_IS_DEVICE_REQUIRED,
  isDeviceRequired,
});

const setRetryFetchingBPIT = (retryFetchingBPIT: boolean): NewTicketAction => ({
  type: Actions.SET_RETRY_FETCHING_BPIT,
  retryFetchingBPIT,
});

const newTicketReducer = (state = initialState, action: NewTicketAction) => {
  const {
    bpitTemplateId,
    category,
    description = '',
    product,
    severity = '',
    subject = '',
  } = action;
  switch (action.type) {
    case Actions.CLEAR_STEP_2:
      return {
        ...state,
        account: {} as Account,
        bpitTemplateId: '',
        product: '',
        payload: {
          ...state.payload,
          accountId: '',
          resources: [],
          severity: '',
        },
      };
    case Actions.REMOVE_RECIPIENT:
      const latestRecipients = state.recipients.filter(
        (recipient) =>
          action.recipient && recipient.id
            ? recipient.id !== action.recipient.id
            : recipient !== action.recipient,
      );
      return {
        ...state,
        recipients: latestRecipients,
        payload: {
          ...state.payload,
          recipients: RecipientUtils.getIds(latestRecipients),
        },
      };
    case Actions.RESET_NEW_TICKET_STATE:
      return {
        ...initialState,
      };
    case Actions.SET_ACCOUNT:
      if (!action.account) {
        return state;
      }
      return {
        ...state,
        account: action.account,
        bpitTemplateId: '',
        recipients: [],
        payload: {
          ...state.payload,
          accountId: action.account.id,
          recipients: [],
          severity: AccountUtils.defaultSeverity(action.account),
          subcategory: '',
        },
      };
    case Actions.SET_BPIT_TEMPLATE_ID:
      return {
        ...state,
        bpitTemplateId,
        recipients: [],
        payload: {
          ...state.payload,
          comment: {
            ...state.payload.comment,
            text: '',
          },
          recipients: [],
          subject: '',
        },
      };
    case Actions.SET_BPIT_TEMPLATE:
      return {
        ...state,
        bpitTemplateId,
        recipients: [],
        payload: {
          ...state.payload,
          category,
          recipients: [],
          subcategory: product,
        },
      };
    case Actions.SET_CATEGORY:
      return {
        ...state,
        account: {} as Account,
        bpitTemplateId: '',
        devices: [],
        payload: {
          ...initialState.payload,
          category: action.category,
        },
      };
    case Actions.SET_DESCRIPTION:
      return {
        ...state,
        payload: {
          ...state.payload,
          comment: {
            ...state.payload.comment,
            text: action.description,
          },
        },
      };
    case Actions.SET_DEVICES:
      const devices = action.devices || [];
      return {
        ...state,
        bpitTemplateId: '',
        devices,
        payload: {
          ...state.payload,
          resources: devices.map((device) => device._links.self),
        },
      };
    case Actions.SET_NEW_RECIPIENTS:
      const recipients = RecipientUtils.unique([
        ...state.recipients,
        ...(action.recipients || []),
      ]);

      return {
        ...state,
        recipients,
        payload: {
          ...state.payload,
          recipients: RecipientUtils.getIds(recipients),
        },
      };
    case Actions.SET_PRODUCT:
      return {
        ...state,
        bpitTemplateId: '',
        recipients: [],
        payload: {
          ...state.payload,
          recipients: [],
          resources: [],
          subcategory: action.product,
        },
      };
    case Actions.SET_REACH_TEMPLATE:
      return {
        ...state,
        recipients: [],
        payload: {
          ...state.payload,
          category,
          comment: {
            ...state.payload.comment,
            text: description || '',
          },
          recipients: [],
          subcategory: product,
          subject,
        },
      };
    case Actions.SET_RESUME_CREATE_TICKET_STATE:
      return {
        ...state,
        payload: {
          ...state.payload,
          category,
          comment: {
            ...state.payload.comment,
            text: description,
          },
          severity,
          subject,
        },
      };
    case Actions.SET_SEVERITY:
      return {
        ...state,
        payload: {
          ...state.payload,
          severity: action.severity,
        },
      };
    case Actions.SET_STEP:
      return {
        ...state,
        selectedStep: action.step,
      };
    case Actions.SET_SUBJECT:
      return {
        ...state,
        payload: {
          ...state.payload,
          subject: action.subject,
        },
      };
    case Actions.SET_ERROR_IN_FETCHING_BPIT:
      return {
        ...state,
        errorInFetchingBPIT: action.errorInFetchingBPIT,
      };
    case Actions.SET_RETRY_FETCHING_BPIT:
      return {
        ...state,
        retryFetchingBPIT: action.retryFetchingBPIT,
      };
    case Actions.SET_IS_DEVICE_REQUIRED:
      return {
        ...state,
        isDeviceRequired: action.isDeviceRequired,
      };
    default:
      return state;
  }
};

/* Thunks */

const getDevices = (account: Account, skusList: Array<string>) => async (
  dispatch: Dispatch<NewTicketAction>,
) => {
  dispatch(setDevices([]));

  const options = {
    dataKey: 'resources',
    filter: ImmutableQuery.fromObject({
      accountId: account.id,
      status: 'all',
      limit: MAXIMUM_RESOURCES_LIMIT,
      permission: 'admin',
      sku: skusList.length > 0 ? skusList.join(',') : null,
    }),
    preRequest: DeviceUtils.textToQ,
    resourceUrl: RESOURCES_URL,
    retries: 0,
  };

  await dispatch(createCollection<Device>(COLLECTIONS.DEVICE, options));
};

const selectNewTicketAccount = (accountId: string) => (
  dispatch: Dispatch<NewTicketAction>,
  getState: Function,
) => {
  const { accounts } = getState() as AppState;
  const account = AccountUtils.findById(accounts.items, accountId);
  dispatch(setAccount(account));
  dispatch(clearCollection<Device>(COLLECTIONS.DEVICE));
};

const selectProduct = (
  account: Account,
  categoryName: string,
  product: string,
) => async (dispatch: Dispatch<NewTicketAction>, getState: Function) => {
  await dispatch(setProduct(product));

  const { categories } = getState() as AppState;
  const category = NewTicketUtils.getCategory(categories.items, categoryName);
  const isTicketAdmin = AccountUtils.isTicketAdmin(account);

  if (
    NewTicketUtils.canSelectDevices(category.subcategories, account, product) &&
    !isTicketAdmin
  ) {
    let skusList: Array<string> = [];
    if (AccountUtils.isRas(account)) {
      const subcategories = NewTicketUtils.getSubcategories(
        category,
        account,
        AccountUtils.isDeviceAdmin(account),
      );
      skusList = NewTicketUtils.getSkusByAccountAndProduct(
        account,
        subcategories,
        product,
      );
    }
    return dispatch(getDevices(account, skusList));
  }
};

const submitBpitTicket = (payload: {}, onUpdate: () => void) => async (
  dispatch: Dispatch<NewTicketAction>,
  getState: () => AppState,
) => {
  const {
    newTicket: { account, bpitTemplateId },
    portalPreferences: {
      interceptSurvey: { lastSurveyed, optOut },
    },
  } = getState();

  const onSuccess = (response: {}, responseHeaders: Headers) => {
    dispatch(resetNewTicketState());

    const isNewTicketDedicated = AccountUtils.isDedicated(account);

    // Define queryparams to indicate a ticket has been created
    const query: NewTicketQueryParams = {
      new_ticket: true,
      new_ticket_id: null,
      new_ticket_dedicated: isNewTicketDedicated || null,
      new_ticket_templateId: bpitTemplateId,
      new_ticket_bpit_processId: responseHeaders.get('x-process-id') || '',
    };

    history.push({
      pathname: '/list',
      search: ImmutableQuery.fromObject(query).toQueryString(),
    });

    if (
      FeatureFlags.isActive('intercept-survey') &&
      isNewTicketDedicated &&
      (PreferencesUtil.showSurvey(optOut, lastSurveyed) ||
        FeatureFlags.isActive('intercept-survey-testing'))
    ) {
      dispatch(openInterceptSurvey());
    }

    if (!isRacker() && FeatureFlags.isActive('resume-ticket')) {
      dispatch(
        deletePortalPreferences(NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT),
      );
    }
  };

  return ApiRequest.post(
    PROCESS_INSTANCES_URL,
    { ...payload, accountId: account.id },
    { dispatchId: RequestId.SUBMIT_BPIT_TICKET, onSuccess, onUpdate },
  );
};

const submitTicket = (newTicket: NewTicket) => async (
  dispatch: Dispatch<NewTicketAction>,
  getState: Function,
) => {
  const state: AppState = getState();
  const { account, payload } = newTicket;

  const payloadWithAttachments = {
    ...payload,
    comment: {
      ...payload.comment,
      attachments: AttachmentUtils.forCommentPost(state.attachments.items),
    },
  };

  const onSuccess = (response: {}) => {
    const isDedicated = AccountUtils.isDedicated(account);

    // Define queryparams to indicate a ticket has been created
    const query: NewTicketQueryParams = {
      new_ticket: true,
      new_ticket_id: MiscUtils.get<string | null>(response, 'ticketId', null),
      new_ticket_dedicated: isDedicated || null,
    };

    history.push({
      pathname: '/list',
      search: ImmutableQuery.fromObject(query).toQueryString(),
    });

    const {
      portalPreferences: {
        interceptSurvey: { lastSurveyed, optOut },
      },
    } = state;

    if (
      FeatureFlags.isActive('intercept-survey') &&
      isDedicated &&
      (PreferencesUtil.showSurvey(optOut, lastSurveyed) ||
        FeatureFlags.isActive('intercept-survey-testing'))
    ) {
      dispatch(openInterceptSurvey());
    }

    if (!isRacker() && FeatureFlags.isActive('resume-ticket')) {
      dispatch(
        deletePortalPreferences(NAMESPACE_PREFERENCES.CREATE_TICKET_DRAFT),
      );
    }
    dispatch(resetNewTicketState());
  };

  // NOTE: For dedicated account only ticketID will load , for cloud account entire response data will load
  const submitTicketQuery: SubmitTicketQueryParams = {
    details: false,
  };

  const isTicketDetailsLoad = ImmutableQuery.fromObject(
    submitTicketQuery,
  ).toQueryString();

  ApiRequest.post(
    `${TICKETS_URL}${isTicketDetailsLoad}`,
    payloadWithAttachments,
    {
      dispatchId: RequestId.SUBMIT_CUSTOM_TICKET,
      onSuccess,
    },
  );
};

export {
  clearStep2,
  getDevices,
  newTicketReducer,
  removeRecipient,
  resetNewTicketState,
  selectNewTicketAccount,
  selectProduct,
  setAccount,
  setBpitTemplate,
  setBpitTemplateId,
  setCategory,
  setDescription,
  setDevices,
  setNewRecipients,
  setProduct,
  setReachTemplate,
  setSeverity,
  setStep,
  setSubject,
  submitBpitTicket,
  submitTicket,
  setResumeTicket,
  setErrorInFetchingBPIT,
  setRetryFetchingBPIT,
  setIsDeviceRequired,
};
